import { PrivyProvider, addRpcUrlOverrideToChain } from '@privy-io/react-auth';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { mainnet, ronin, saigon } from 'viem/chains';
import 'bootstrap/dist/css/bootstrap.min.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { WagmiProvider, http, createConfig } from 'wagmi';
import { ronin as wagmiRonin, saigon as wagmiSaigon } from 'wagmi/chains';
import { metaMask } from 'wagmi/connectors';
import * as Sentry from '@sentry/react';

import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import GameUpdateModal from './components/GameUpdateModal';
import PopupNotificationMessage from './components/PopupNotification';
import environments from './utils/environments';
import { monitoring } from './utils/monitoring';

const { PUBLIC_RPC_URL, ENVIRONMENT, GAME_VERSION, RPC_URL, SENTRY_DSN, CHAIN_ID } = environments;

Sentry.init({
  environment: ENVIRONMENT,
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'assert'],
    }),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    'localhost',
    'https://fishing-frenzy-staging-new-feature.web.app',
    'https://fishingfrenzy.co',
    'https://smoketest.fishingfrenzy.co',
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  // iOS-specific context
  beforeSend(event, hint) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      event.tags = {
        ...event.tags,
        platform: 'iOS',
        standalone: window.navigator.standalone ? 'yes' : 'no',
      };

      // Add memory info if available
      if (performance.memory) {
        event.contexts = {
          ...event.contexts,
          memory: {
            used: performance.memory.usedJSHeapSize,
            total: performance.memory.totalJSHeapSize,
            limit: performance.memory.jsHeapSizeLimit,
          },
        };
      }
    }

    return event;
  },
});

Sentry.setTag('game.type', 'cocos2d');
Sentry.setTag('app.type', 'PWA');

saigon.id = Number(CHAIN_ID);
const queryClient = new QueryClient();
const wagmiConfig = createConfig({
  chains: ENVIRONMENT === 'production' ? [wagmiRonin] : [wagmiSaigon],
  connectors: [metaMask()],
  transports: {
    [ronin.id]: http(RPC_URL),
    [saigon.id]: http(RPC_URL),
  },
});

let baseCustomRPC;

if (process.env.REACT_APP_ENVIRONMENT === 'prod') {
  baseCustomRPC = addRpcUrlOverrideToChain(ronin, PUBLIC_RPC_URL);
} else {
  baseCustomRPC = addRpcUrlOverrideToChain(saigon, PUBLIC_RPC_URL);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const isRenderedInIframe = window.parent !== window;
const basicLoginMethods = ['email', 'sms', 'wallet'];
const socialLoginMethods = ['google', 'twitter', 'telegram'];

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <PrivyProvider
          appId={process.env.REACT_APP_PRIVY_APP_ID}
          config={{
            appearance: {
              walletList: ['metamask', 'ronin_wallet', 'wallet_connect_qr'],
            },
            // google & X does not work in iframe
            loginMethods: isRenderedInIframe ? basicLoginMethods : [...basicLoginMethods, ...socialLoginMethods],
            defaultChain: baseCustomRPC,
            supportedChains: [mainnet, baseCustomRPC],
            embeddedWallets: {
              ethereum: { createOnLogin: 'all-users' },
              noPromptOnSignature: true,
              waitForTransactionConfirmation: true,
              priceDisplay: {
                primary: 'native-token', // Defaults to 'fiat-currency'
                secondary: null, // Defaults to 'native-token'
              },
              showWalletUIs: false,
            },
          }}>
          <WagmiProvider config={wagmiConfig}>
            <QueryClientProvider client={queryClient}>
              <App />
              <GameUpdateModal />
              <PopupNotificationMessage />
            </QueryClientProvider>
          </WagmiProvider>
        </PrivyProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

function ErrorFallback() {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h2>Game encountered an error</h2>
      <button onClick={() => window.location.reload()}>Reload Game</button>
    </div>
  );
}

// iOS-Specific Memory Monitoring
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

if (isIOS) {
  // Track visibility changes (backgrounding)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'App backgrounded',
        level: 'info',
      });
    } else {
      Sentry.addBreadcrumb({
        category: 'navigation',
        message: 'App foregrounded',
        level: 'info',
      });
    }
  });

  // RUNTIME Memory monitoring - check continuously during gameplay
  if (performance.memory) {
    let lastMemoryWarning = 0;
    let memoryGrowthRate = 0;
    let previousMemory = performance.memory.usedJSHeapSize;

    monitoring.intervalId = setInterval(() => {
      const memoryUsed = performance.memory.usedJSHeapSize;
      const memoryLimit = performance.memory.jsHeapSizeLimit;
      const memoryPercent = (memoryUsed / memoryLimit) * 100;

      // Calculate memory growth rate (per minute)
      const memoryGrowth = memoryUsed - previousMemory;
      memoryGrowthRate = (memoryGrowth / 1048576) * 6; // MB per minute
      previousMemory = memoryUsed;

      console.log(
        `iOS Memory Usage: ${(memoryUsed / 1048576).toFixed(2)} MB / ${(memoryLimit / 1048576).toFixed(
          2
        )} MB (${memoryPercent.toFixed(2)}%), Growth Rate: ${memoryGrowthRate.toFixed(2)} MB/min`
      );
      // Warn at different thresholds with rate limiting
      const now = Date.now();
      if (memoryPercent > 90 && now - lastMemoryWarning > 30000) {
        Sentry.captureMessage('CRITICAL: Memory usage above 90%', {
          level: 'error',
          extra: {
            usedMB: (memoryUsed / 1048576).toFixed(2),
            limitMB: (memoryLimit / 1048576).toFixed(2),
            percent: memoryPercent.toFixed(2),
            growthRateMBPerMin: memoryGrowthRate.toFixed(2),
          },
        });
        lastMemoryWarning = now;
      } else if (memoryPercent > 75 && now - lastMemoryWarning > 60000) {
        Sentry.captureMessage('High memory usage during gameplay', {
          level: 'warning',
          extra: {
            usedMB: (memoryUsed / 1048576).toFixed(2),
            limitMB: (memoryLimit / 1048576).toFixed(2),
            percent: memoryPercent.toFixed(2),
            growthRateMBPerMin: memoryGrowthRate.toFixed(2),
          },
        });
        lastMemoryWarning = now;
      }

      // Warn about memory leaks (consistent growth)
      if (memoryGrowthRate > 5 && now - lastMemoryWarning > 60000) {
        Sentry.captureMessage('Possible memory leak detected', {
          level: 'warning',
          extra: {
            growthRateMBPerMin: memoryGrowthRate.toFixed(2),
            currentUsedMB: (memoryUsed / 1048576).toFixed(2),
          },
        });
        lastMemoryWarning = now;
      }
    }, 10000); // Check every 10 seconds during gameplay
  }

  // Track page unload (might catch crashes)
  window.addEventListener('pagehide', () => {
    Sentry.addBreadcrumb({
      category: 'lifecycle',
      message: 'Page unloading',
      level: 'info',
    });
  });
}

// Monitor long tasks that might cause iOS to kill the app
if ('PerformanceObserver' in window) {
  try {
    monitoring.observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // Long tasks over 100ms can cause iOS issues
        if (entry.duration > 100) {
          console.warn(`Long task detected: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
          Sentry.captureMessage('Long task detected during gameplay', {
            level: 'warning',
            extra: {
              duration: entry.duration.toFixed(2) + 'ms',
              startTime: entry.startTime.toFixed(2),
              name: entry.name,
            },
          });
        }
      }
    });
    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    // longtask not supported, that's ok
  }
}

serviceWorkerRegistration.registerCacheSW(GAME_VERSION);
serviceWorkerRegistration.handleFirstVisitWithoutLogin();
